import {AfterViewInit, ChangeDetectionStrategy, Component, OnInit, ViewChild} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {AuthenticationService} from "../modules/common/services/authentication/authentication.service";
import {ApplicationType, Permission} from "../modules/common/enums/enums";
import {Localization} from '../modules/common/enums/localization';
import {PermissionsService} from "../modules/common/services/permissions-service";
import {SubscriptionService} from "../modules/common/services/subscription.service";
import {TokenService} from "../modules/common/services/token.service";
import {AppMessage} from "../modules/common/model/app-message";
import {MessagingCenterService} from "../modules/common/services/messaging-center.service";
import {AccountSiteService} from "../modules/common/services/account-site.service";
import {AppConfig} from "../config/app.config";
import {combineLatest, Observable, Subject, timer} from "rxjs";
import {AppsListComponent} from "@npm.chapps.com/chapps.common.angular.library";
import * as moment from "moment";
import {LocalizationService} from "../services/localization.service";
import {map} from 'rxjs/operators';
import {
  NewResidentNotificationDialogComponent
} from "../modules/residents/notifications/new-resident-notification-dialog/new-resident-notification-dialog.component";
import {Destroyable, takeUntilDestroyed} from "../angular/component/destruction/destroyable.interface";
import {SelfDestruct} from "../angular/component/destruction/self-destruct";
import {IssueMessage} from "../modules/common/model/issue-message";
import {AttentionPointMessage} from "../modules/common/model/attention-point-message";
import {SystemNotificationsService} from "../modules/system-notifications/system-notifications.service";
import {MatDialog} from '@angular/material/dialog';
import {Environment} from "../environment/environment";

@Component({
  selector: "app-navbar-component",
  templateUrl: "./app-navbar.component.html",
  styleUrls: ["./app-navbar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppNavbarComponent implements OnInit, AfterViewInit, Destroyable {
  @ViewChild(AppsListComponent, {static: true}) appsListComponent: AppsListComponent;
  public destroyed$ = new Subject<AppNavbarComponent>();
  public isBuildingsButtonActive: boolean;
  public isPaymentsButtonActive: boolean;
  public isContactsButtonActive: boolean;
  public areSettingsActive: boolean;
  public areAccountsActive: boolean;
  public areReportSettingsActive: boolean;
  public areRolesActive: boolean;
  public isBusinessActive: boolean;
  public areDevicesActive: boolean;
  public arePartnersActive: boolean;
  public isLicenseActive: boolean;
  public isExportActive: boolean;
  public isImportActive: boolean;
  public isPaymentActive: boolean;
  public isAccountGroupActive: boolean;
  public isInspectionsButtonActive: boolean;
  public isFeedbackActive: boolean;
  public isUserOnDemo: boolean;
  public demoSubscriptionDaysLeft: number;
  public demoSubscriptionExpireDate: string;
  public residenzMessages: AppMessage[];
  public attentionPoints: AttentionPointMessage[];
  public issues: IssueMessage[];
  public updatesUrl: string;
  public readonly Localization = Localization;
  private readonly ROLES: string = "roles";
  public maintenanceUrl: string;
  public interventionTeamsUrl: string;
  public isIssuesButtonActive: boolean;
  public readonly activeSystemNotifications$ = this.systemNotifications.active$.pipe(
      takeUntilDestroyed(this),
  );

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private subscriptionService: SubscriptionService,
    private messagingCenterService: MessagingCenterService,
    private tokenService: TokenService,
    private accountSiteService: AccountSiteService,
    private localizationService: LocalizationService,
    public appConfig: AppConfig,
    public permissionsService: PermissionsService,
    public dialog: MatDialog,
    private systemNotifications: SystemNotificationsService,
    private readonly environment: Environment,
  ) {
    this.updatesUrl = this.environment.updatesUrl + this.localizationService.getLanguangeForZendesk();
    this.maintenanceUrl = `${this.environment.maintenanceUrl}/jobs/list`;
    this.interventionTeamsUrl = `${this.environment.maintenanceUrl}/intervention-teams`
  }

  public get hasMaintenanceProduct(): boolean {
    return this.tokenService.hasApplication(ApplicationType.MAINTENANCE);
  }

  ngOnInit() {
    this.appsListComponent.init(this.accountSiteService.getApplications());
    this.residenzMessages = [];
    this.attentionPoints = [];
    this.issues = [];
    this.isBuildingsButtonActive = false;
    this.isIssuesButtonActive = false;
    this.isPaymentsButtonActive = false;
    this.isContactsButtonActive = false;
    this.areSettingsActive = false;
    this.areAccountsActive = false;
    this.areReportSettingsActive = false;
    this.areRolesActive = false;
    this.isBusinessActive = false;
    this.isLicenseActive = false;
    this.isExportActive = false;
    this.isImportActive = false;
    this.isPaymentActive = false;
    this.isAccountGroupActive = false;
    this.isInspectionsButtonActive = false;
    this.isFeedbackActive = false;
    this.isUserOnDemo = this.subscriptionService.isUserOnDemo();
    this.selectButtonDependingOfRoute(this.router.url);
    this.selectListItemDependingOnRoute(this.router.url);
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.selectButtonDependingOfRoute(event.url);
        this.selectListItemDependingOnRoute(event.url);
      }
      this.isUserOnDemo = this.subscriptionService.isUserOnDemo();
    });
    if (
      this.tokenService.getLicenseAgreed() &&
      this.subscriptionService.isUserOnDemo()
    ) {
      this.getDemoSubscriptionDaysLeft();
    }
    this.loadMessages();

    this.subscriptionService.$subscriptionChanged
      .subscribe(() => {
        this.isUserOnDemo = this.subscriptionService.isUserOnDemo();
      });
  }

  ngAfterViewInit(): void {
    // resize event needs to be called to fix the MDB nav bar component issue. Waiting for Angular 8 upgrade...
    timer(0).subscribe(() => window.dispatchEvent(new Event("resize")));
  }

  public ngOnDestroy() {
    SelfDestruct(this);
  }

  public hasLiteSubscription() : boolean {
    return this.subscriptionService.isUserLite();
  }

  public getDemoSubscriptionDaysLeft() {
    this.subscriptionService
      .getDemoExpirationDate()
      .subscribe(expirationDate => {
        if (expirationDate == null) {
          this.demoSubscriptionDaysLeft = null;
        } else {
          setTimeout(() => {
            this.demoSubscriptionDaysLeft = moment(expirationDate).diff(
              moment(new Date()),
              "days"
            );
            this.demoSubscriptionExpireDate = expirationDate;
          }, 0)
        }
      });
  }

  public goToSubscriptionPage() {
    this.router.navigateByUrl("payment");
  }

  public goToMessageRedirectUrl(url: string) {
    if (url.includes("http")) {
      window.open(url, this.appConfig.RESIDENZ_MANAGER_TAB_NAME);
    } else {
      this.router.navigateByUrl(url);
    }
  }

  public unreadNotifications(): boolean {
    return (
      !this.isResidenzMessagesListEmpty() ||
      !this.isAttentionPointsListEmpty() ||
      !this.isIssuesListEmpty()
    );
  }

  public isResidenzMessagesListEmpty(): boolean {
    return this.residenzMessages.length === 0;
  }

  public isAttentionPointsListEmpty(): boolean {
    return this.attentionPoints.length === 0;
  }

  public isIssuesListEmpty(): boolean {
    return this.issues.length === 0;
  }

  public getDefaultSelectedTab() : number {
    return !this.isResidenzMessagesListEmpty() ? 0 :
           !this.isAttentionPointsListEmpty() ? 1 :
           !this.isIssuesListEmpty() ? 2 : 0;
  }

  public getUsername(): string {
    return `${this.tokenService.getFirstName()} ${this.tokenService.getLastName()}`;
  }

  public getUserEmail(): string {
    return this.tokenService.getUsername();
  }

  public getLoginId(): number {
    return this.tokenService.getLoginId();
  }

  public hasResidenzProduct(): boolean {
    return this.tokenService.hasApplication(ApplicationType.RESIDENZ);
  }

  public navigateToProfile() {
    this.router.navigate(["/accounts/" + this.getLoginId()]);
  }

  public shouldShowOnlyAccountSection(): boolean {
    return !this.authService.doesUserHaveActiveSubscription();
  }

  public canUserViewBuildings(): boolean {
    return this.authService.doesUserHaveActiveSubscription();
  }

  public canUserViewContacts(): Observable<boolean> {
    return this.permissionsService.doesUserHavePermissionToViewContacts();
  }

  public canUserViewRoles(): Observable<boolean> {
    return this.permissionsService.doesUserHavePermissionToViewRoles();
  }

  public canUserViewAccounts(): Observable<boolean> {
    return this.permissionsService.doesUserHavePermissionToViewAccounts();
  }

  public canUserViewReportSettings(): Observable<boolean> {
    return combineLatest(
      [this.permissionsService.doesUserHavePermission(Permission.REPORTSETTINGSVIEW),
        this.permissionsService.doesUserHavePermissionToViewAccounts()]
      ).pipe(map(([reportFeature, viewAccountsPerm]) => reportFeature && viewAccountsPerm));
  }

  public canUserViewPayment(): Observable<boolean> {
    return this.permissionsService.doesUserHavePermissionToViewPayment();
  }

  public hasInspectionFeature() {
    return this.permissionsService.doesUserHavePermission(Permission.INSPECTIONSVIEW);
  }

  public forceDisplayPaymentWizardInMenu(): boolean {
    return !this.authService.doesUserHaveActiveSubscription();
  }

  selectButtonDependingOfRoute(url: string) {
    this.isBuildingsButtonActive = url.includes("buildings");
    this.isIssuesButtonActive = url.startsWith("/issues");
    this.isContactsButtonActive =
      url.includes("contacts") &&
      !url.includes("buildings") &&
      !url.includes("import");
    this.isInspectionsButtonActive =
      url.includes("inspections") && !url.includes("buildings");
    this.areSettingsActive =
      url.includes("accounts") ||
      url.includes("report") ||
      url.includes(this.ROLES) ||
      url.includes("devices") ||
      url.includes("export") ||
      url.includes("import") ||
      url.includes("feedback");
    this.isAccountGroupActive =
      url.includes("license") ||
      url.includes("business") ||
      (url.includes("subscription") && !url.includes("payment"));
  }

  selectListItemDependingOnRoute(url: string) {
    this.areReportSettingsActive = url.includes("report");
    this.areAccountsActive = url.includes("accounts");
    this.areRolesActive = url.includes(this.ROLES);
    this.isBusinessActive = url.includes("business");
    this.areDevicesActive = url.includes("devices");
    this.arePartnersActive = url.includes("partners");
    this.isLicenseActive = url.includes("license");
    this.isExportActive = url.includes("export");
    this.isImportActive = url.includes("import");
    this.isPaymentActive = url.includes("payment");
    this.isFeedbackActive = url.includes("feedback");
  }

  logout() {
    this.accountSiteService.goToSignOutPage();
  }

  private loadMessages() {
    this.messagingCenterService.residenzNotifications$
        .pipe(takeUntilDestroyed(this))
        .subscribe(messages => this.residenzMessages = messages);
    this.messagingCenterService.attentionPointMessage$
        .pipe(takeUntilDestroyed(this))
        .subscribe(messages => this.attentionPoints = messages);
    this.messagingCenterService.issueMessages$
        .pipe(takeUntilDestroyed(this))
        .subscribe(messages => this.issues = messages);
  }

  public onEvent(event) {
    event.stopPropagation();
  }

  public onDropdownClicked(event) {
    window.dispatchEvent(new Event('resize')); //Workaround for this issue with angular tabs: https://github.com/angular/components/issues/7274
  }

  public addNewResidentNotification() {
    const dialog = this.dialog.open(NewResidentNotificationDialogComponent, { panelClass: 'full-bleed' });
  }

    protected readonly moment = moment;


}
